import {
  Add as AddIcon
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import { useSelector } from "react-redux";

const API_DOMAIN = "https://estbn.com/EstbnNew/public/api/";
const PROPERTIES_ENDPOINT = API_DOMAIN + "portal/properties";


const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "16px",
  p: 0,
  display: "flex",
  flexDirection: "column",
  maxHeight: "90vh",
};

const headerFooterStyle = {
  p: "8px 24px 20px 24px",
};

const contentStyle = {
  flexGrow: 1,
  overflowY: "auto",
  p: "0px 5px 16px 24px",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#f1f1f1",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#888",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#555",
  },
};

const InfoTextItem = ({ label, value }) => (
  <Typography variant="body1" sx={{ mb: 0.8 }}>
    {label}:{" "}
    <Typography component="span" fontWeight="bold">
      {value}
    </Typography>
  </Typography>
);

const UpdateProperties = ({
  open,
  onClose,
  initialPropertyData,
  onSaveSuccess,
  onDeleteSuccess,
  getCategoryById,
  handleOpenEditPropertyFormModal,
}) => {
  const defaultData = {
    id: null,
    name: "N/A",
    type: "String",
    isFilterable: false,
    isRequired: false,
    isGeneral: false,
    showToUser: false,
    sortOrder: 0,
    values: [], 
    categoryId: null,

  };

  const { user } = useSelector((state) => state.auth);

  const AUTH_TOKEN = user?.token;

  const [propertyData, setPropertyData] = useState({ ...defaultData });
  const [propertyValues, setPropertyValues] = useState([]);
  const [valueErrors, setValueErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false); 
  const [isDeleting, setIsDeleting] = useState(false); 
  const [apiError, setApiError] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showToUser, setShowToUser] = useState(false);
  const [originalPropertyValues, setOriginalPropertyValues] = useState([]); 
  const [valuesToDelete, setValuesToDelete] = useState([]); 

  useEffect(() => {
    if (open) {
      setApiError("");
      setIsLoading(false);
      setIsDeleting(false);
      setShowDeleteConfirm(false);
      if (initialPropertyData) {
        setPropertyData({
          id: initialPropertyData.id,
          name: initialPropertyData.name,
          type: initialPropertyData.type,
          isFilterable: initialPropertyData.is_filterable,
          isRequired: initialPropertyData.is_required,
          isGeneral: initialPropertyData.is_general,
          showToUser: initialPropertyData.show_to_user,
          sortOrder: initialPropertyData.sort_order,
          categoryId: initialPropertyData.category_id,
        });

        const initialValuesMapped =
          Array.isArray(initialPropertyData.values) &&
          initialPropertyData.values.length > 0
            ? initialPropertyData.values.map((v, idx) => ({
                id: v.id !== undefined ? v.id : null, 
                value: v.value !== undefined ? String(v.value) : String(v), 
                sort_order: v.sort_order !== undefined ? v.sort_order : idx + 1,
                show_to_user: v.show_to_user  
              }))
            : [];

        setPropertyValues(
          initialValuesMapped.length > 0
            ? initialValuesMapped
            : [{ id: null, value: "", sort_order: 1, show_to_user: false }]
        );
        setOriginalPropertyValues(initialValuesMapped); 
        setValueErrors(
          new Array(
            initialValuesMapped.length > 0 ? initialValuesMapped.length : 1
          ).fill("")
        );
        setValuesToDelete([]);
      } else {
        setPropertyData({ ...defaultData });
        setPropertyValues([{ id: null, value: "", sort_order: 1 , show_to_user:false}]); 
        setValueErrors([""]);
        setOriginalPropertyValues([]);
        setValuesToDelete([]);
      }
    }
  }, [initialPropertyData, open]);

  const validatePropertyValues = () => {
    let hasError = false;

    if (propertyData.type === "select" && propertyData.isRequired) {
      const cleanedValues = propertyValues
        .map((v) => v.value.trim())
        .filter((v) => v);
      const newErrors = propertyValues.map((item) => {
        if (!item.value.trim()) {
          hasError = true;
          return "This field is required.";
        }
        return "";
      });
      setValueErrors(newErrors);

      if (cleanedValues.length === 0 && propertyData.isRequired) {
        hasError = true;
        if (
          propertyValues.length === 0 ||
          (propertyValues.length === 1 && !propertyValues[0].value.trim())
        ) {
          setPropertyValues([{ id: null, value: "", sort_order: 1 }]); 
          setValueErrors(["At least one value is required."]);
        } else {
          const allEmptyErrors = propertyValues.map((item) =>
            item.value.trim() === "" ? "At least one value is required." : ""
          );
          setValueErrors(allEmptyErrors);
        }
      }
    }
    return hasError;
  };

  const handleAddValue = () => {
    const newSortOrder =
      propertyValues.length > 0
        ? Math.max(...propertyValues.map((v) => v.sort_order)) + 1
        : 1;
    setPropertyValues([
      ...propertyValues,
      { id: null, value: "", sort_order: newSortOrder , show_to_user: false},
    ]);
    setValueErrors([...valueErrors, ""]);
  };

  const handleValueChange = (index, newValue) => {
    const newValues = [...propertyValues];
    newValues[index] = { ...newValues[index], value: newValue };
    setPropertyValues(newValues);
    if (valueErrors[index] && newValue.trim()) {
      const newErrors = [...valueErrors];
      newErrors[index] = "";
      setValueErrors(newErrors);
    }
  };

  const handleRemoveValue = (index) => {
    const valueToRemove = propertyValues[index];
    if (valueToRemove.id !== null && valueToRemove.id !== undefined) {
      setValuesToDelete((prev) => [...prev, valueToRemove.id]);
    }

    const newValues = propertyValues.filter((_, i) => i !== index);
    const newErrors = valueErrors.filter((_, i) => i !== index);

    if (
      propertyData.type === "select" &&
      propertyData.isRequired &&
      newValues.filter((v) => v.value.trim()).length === 0
    ) {
      setPropertyValues([{ id: null, value: "", sort_order: 1 }]); 
      setValueErrors(["At least one value is required."]);
    } else {
      const reorderedValues = newValues.map((v, i) => ({
        ...v,
        sort_order: v.sort_order || i + 1,
      }));
      setPropertyValues(
        reorderedValues.length > 0
          ? reorderedValues
          : [{ id: null, value: "", sort_order: 1 }]
      );
      setValueErrors(newErrors.length > 0 ? newErrors : [""]);
    }
  };

  const handleApiSave = async () => {
    setApiError("");

    if (
      ["select", "text"].includes(propertyData.type) &&
      propertyData.isRequired
    ) {
      if (validatePropertyValues()) {
        
        setApiError("Please fill in all required property values.");
        return;
      }
    }

    if (!initialPropertyData?.id || !propertyData?.categoryId) {
      setApiError("Property or Category ID is missing. Cannot update.");
      return;
    }

    setIsLoading(true);
    let success = true; 
    let updatedPropertyData = null;

    try {
   
      // const propertyFormData = new FormData();
      // propertyFormData.append("name", propertyData.name);
      // propertyFormData.append("type", propertyData.type);
      // propertyFormData.append(
      //   "is_filterable",
      //   propertyData.isFilterable ? "1" : "0"
      // );
      // propertyFormData.append(
      //   "is_required",
      //   propertyData.isRequired ? "1" : "0"
      // );
      // propertyFormData.append(
      //   "is_general",
      //   propertyData.is_general ? "1" : "0"
      // );
      // propertyFormData.append(
      //   "show_to_user",
      //   propertyData.show_to_user ? "1" : "0"
      // );
      // propertyFormData.append("sort_order", propertyData.sortOrder);
      // propertyFormData.append("category_id", propertyData.categoryId);
      // propertyFormData.append("_method", "PUT"); 

      // const propertyUpdateResponse = await axios.post(
      //   `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}`,
      //   propertyFormData,
      //   { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
      // );

      // if (!propertyUpdateResponse.data?.success) {
      //   setApiError(
      //     propertyUpdateResponse.data.message ||
      //       "Failed to update property metadata."
      //   );
      //   setIsLoading(false);
      //   return; 
      // }
      // updatedPropertyData = propertyUpdateResponse.data.data;

      if (["select", "text"].includes(propertyData.type)) {
        const operations = [];
        const originalValuesMap = new Map(
          originalPropertyValues.map((v) => [v.id, v])
        );

        propertyValues.forEach((currentValue) => {
       

          if (currentValue.id === null || currentValue.id === undefined) {
          
            operations.push(async () => {
              const formData = new FormData();
    
              formData.append("values[0][value]", currentValue.value);
              formData.append("values[0][sort_order]", currentValue.sort_order); 
              if(currentValue.show_to_user){
              formData.append("values[0][show_to_user]", 1); 
              }
              
              try {
                const response = await axios.post(
                  `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values`,
                  formData,
                  { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
                );
               
                return response.data.data; 
              } catch (err) {
                console.log(
                  err.response?.data?.message || err.message
                );
              }
            });
          } else {
            const originalValue = originalValuesMap.get(currentValue.id);
            if (
              !valuesToDelete.includes(currentValue.id) &&
              originalValue &&
              (originalValue.value !== currentValue.value ||
                originalValue.sort_order !== currentValue.sort_order)
            ) {
              operations.push(async () => {
                const formData = new FormData();
                formData.append("value", currentValue.value);
                formData.append("sort_order", currentValue.sort_order);
                formData.append("_method", "PUT"); 

                try {
                  const response = await axios.post(
                    `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values/${currentValue.id}`,
                    formData,
                    { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
                  );
                 
                  return response.data.data;
                } catch (err) {
                  console.log(
                    err.response?.data?.message || err.message
                  );
                }
              });
            }
          }
        });

        if (valuesToDelete.length > 0) {
          operations.push(async () => {
            try {
              const bulkDeleteFormData = new FormData();
              valuesToDelete.forEach((id, idx) => {
                bulkDeleteFormData.append(`values[${idx}]`, id); 
              });

              const deleteResponse = await axios.post(
                `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values`,
                bulkDeleteFormData,
                {
                  headers: {
                    Authorization: `Bearer ${AUTH_TOKEN}`,
                    "X-HTTP-Method-Override": "DELETE", 
                  },
                }
              );
              if (!deleteResponse.data?.success)
                throw new Error(
                  deleteResponse.data.message || `Failed to delete bulk values.`
                );
              console.log(
                `Bulk deleted values ${valuesToDelete.join(", ")} successfully.`
              );
            } catch (err) {
              console.warn(
                `Bulk delete failed or API does not support DELETE with body (Error: ${
                  err.response?.data?.message || err.message
                }). Attempting individual deletes.`
              );
              const individualDeletePromises = valuesToDelete.map(
                async (id) => {
                  try {
                    const individualDeleteResponse = await axios.delete(
                      `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}/values/${id}`,
                      { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
                    );
                    if (!individualDeleteResponse.data?.success)
                      throw new Error(
                        individualDeleteResponse.data.message ||
                          `Failed to delete value ID: ${id}`
                      );
                    console.log(`Individually deleted value ID: ${id}`);
                  } catch (indErr) {
                    console.error(
                      `Error deleting individual value ID: ${id}:`,
                      indErr.response?.data?.message || indErr.message
                    );
                  }
                }
              );
              await Promise.allSettled(individualDeletePromises); 
            }
          });
        }

        const results = await Promise.allSettled(operations.map((op) => op()));

        const failedValueOperations = results.filter(
          (result) => result.status === "rejected"
        );
        if (failedValueOperations.length > 0) {
          console.error("Some value operations failed:", failedValueOperations);
          setApiError(
            (prev) =>
              (prev ? prev + "\n" : "") +
              "Some property value changes could not be saved/deleted. Check console for details."
          );
         
        }
      }
    } catch (err) {
      const errorMsg =
        err.response?.data?.message ||
        "An error occurred during save operation.";
      setApiError(errorMsg);
      success = false;
    } finally {
      setIsLoading(false);
      if (success) {
        onSaveSuccess?.(updatedPropertyData || initialPropertyData);
        onClose?.();
      }
      getCategoryById();
    }
  };

  const handleDeleteIconClick = () => {
    setApiError("");
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!initialPropertyData?.id) {
      setApiError("Property ID is missing. Cannot delete.");
      return;
    }
    setIsDeleting(true);
    setApiError("");
    try {
      const response = await axios.delete(
        `${PROPERTIES_ENDPOINT}/${initialPropertyData.id}`,
        { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } }
      );
      if (response.data?.success) {
        getCategoryById?.();
        onDeleteSuccess?.(initialPropertyData.id);
        setShowDeleteConfirm(false);
        onClose?.();
      } else {
        setApiError(response.data.message || "Failed to delete property.");
      }
    } catch (err) {
      setApiError(
        err.response?.data?.message ||
          "An error occurred while deleting the property."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  



  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={modalStyle}>
          <Box
            sx={{
              ...headerFooterStyle,
              padding: "10px 20px",
              borderBottom: "1px solid #eee",
            }}
          >
            <Typography variant="h6" fontWeight="bold" width={"75%"}>
              Property Profile
            </Typography>
         
          </Box>

          <Box sx={contentStyle}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                mb: 1,
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Property Info
              </Typography>
            </Box>

            <Grid container spacing={0.3}>
              <Grid item xs={6}>
                <InfoTextItem label="Name" value={propertyData.name} />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem label="Type" value={propertyData.type} />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Is Filterable"
                  value={propertyData.isFilterable ? "True" : "False"}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Is Required"
                  value={propertyData.isRequired ? "True" : "False"}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Sort Order"
                  value={propertyData.sortOrder}
                />
              </Grid>
              <Grid item xs={6}>
                <InfoTextItem
                  label="Category ID"
                  value={propertyData?.categoryId || "N/A"}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 0.8, borderColor: "rgba(0, 0, 0, 0.12)" }} />

            <Typography variant="h6" fontWeight="bold" mt={0.5} mb={1.5}>
              Property Values
            </Typography>
            {/* MODIFIED: Now also shows value inputs for 'text' type */}
            {["select", "text"].includes(propertyData.type) ? (
              <>
                {propertyValues.map((item, index) => (
              <>
                  <Box
                    key={item.id || `new-${index}`}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { sm: "center" },
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <TextField
                      fullWidth
                      className="mt-1"
                      size="small"
                      label={
                        index === 0 ? "Property Value" : `Value ${index + 1}`
                      }
                      placeholder="Add property value" 
                      value={item.value}
                      onChange={(e) => handleValueChange(index, e.target.value)}
                      error={!!valueErrors[index]}
                      helperText={valueErrors[index]} 
                      disabled={isLoading || isDeleting}
                    />
          
                    {(propertyValues.length > 1 ||
                      (propertyValues.length === 1 &&
                        item.value.trim() !== "")) && (
                      <IconButton
                        onClick={() => handleRemoveValue(index)}
                        color="error"
                        disabled={isLoading || isDeleting}
                        aria-label={`Remove value ${index + 1}`}
                        className="mb-2"
                      >
                        <Trash2 size={20} />
                        {/* <DeleteIcon /> */}
                      </IconButton>
                    )}

                  </Box>
                  <div>
                               <Form.Check
            type="checkbox"
            id="showToUser"
            label="Show To User"
            // checked={item.show_to_user} 
            onChange={(e)=>setShowToUser(e.target.checked)}
            disabled={isLoading}
            className="my-2"
         
          />
                  </div>
              </>
                ))}

          
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <Button
                    startIcon={<AddIcon fontSize="small" />}
                    onClick={handleAddValue}
                    disabled={isLoading || isDeleting}
                    size="small"
                    sx={{
                      textTransform: "none",
                      mt: 0.5,
                      color: "text.secondary",
                    }}
                  >
                    Add another value
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Values are not applicable for properties of type '
                {propertyData.type}'.
              </Typography>
            )}

            {apiError && !showDeleteConfirm && (
              <Typography color="error" mt={2} sx={{ wordBreak: "break-word" }}>
                {apiError}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              ...headerFooterStyle,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              onClick={onClose}
              disabled={isLoading || isDeleting}
              variant="outlined"
              className=" w-25 h-100"
              sx={{
                color: "common.black",
                borderColor: "grey.400",
                "&:hover": {
                  backgroundColor: "grey.100",
                  borderColor: "grey.500",
                },
                borderRadius: "8px",
                // padding: "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className=" w-25 h-100"
              onClick={handleApiSave}
              disabled={isLoading || isDeleting}
              sx={{
                backgroundColor: "common.black",
                color: "common.white",
                "&:hover": { backgroundColor: "grey.800" },
                borderRadius: "8px",
                // padding: "6px 16px",
                boxShadow: "none",
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal (remains unchanged) */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
      >
        <Box sx={{ ...modalStyle, width: 400, p: 0 }}>
          <Box
            sx={{
              ...headerFooterStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Confirm Deletion
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <Typography mb={3}>
              Are you sure you want to delete "{initialPropertyData?.name}"?
            </Typography>
            {apiError && showDeleteConfirm && (
              <Typography color="error" mb={2} sx={{ wordBreak: "break-word" }}>
                {apiError}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              ...headerFooterStyle,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              borderTop: "1px solid #eee",
            }}
          >
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              variant="outlined"
              sx={{
                color: "common.black",
                borderColor: "grey.400",
                "&:hover": {
                  backgroundColor: "grey.100",
                  borderColor: "grey.500",
                },
                borderRadius: "8px",
                padding: "6px 16px",
              }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
              disabled={isDeleting}
              sx={{
                borderRadius: "8px",
                padding: "6px 16px",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.error.dark,
                },
              }}
            >
              {isDeleting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Delete"
              )}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default UpdateProperties;
